import usePageTitle from "@/hooks/usePageTitle";
import FocusedPageLayout from "@/layouts/FocusedPageLayout";
import { useParams, useOutletContext } from "react-router-dom";
import { useEffect, useState } from "react";
import { InteractionDetails } from "@/components/interactions";
import ErrorBoundary from "@/components/ErrorBoundary";
import { JobsOutletContext } from "@/pages/_layouts/jobs/JobPostIndexLayout";
import InteractionsPageSkeleton from "@/pages/_layouts/jobs/_components/InteractionsPageSkeleton";
import { SOME_PEOPLE_QUERY } from "@/pages/people/queries";
import { useQuery } from "@apollo/client";
import { useBackNavigation } from "@/hooks/useBackNavigation";

export default function InteractionShowPage() {
  usePageTitle(
    "/sections/:sectionId/jobs/job-post/:jobPostId/interaction/:interactionId",
    "Sections | Section Details | Jobs | Job Post Details | Interaction Details"
  );
  const [actions, setActions] = useState<React.ReactNode>(null);
  const [selectedPerson, setSelectedPerson] = useState<string | null>(null);
  const { onBackClick, backLink } = useBackNavigation();

  const { sectionId, jobPostId, interactionId } = useParams();

  const { data: pgaIdData } = useQuery(SOME_PEOPLE_QUERY, {
    variables: {
      input: { search: selectedPerson ? `${selectedPerson}` : "" },
      first: 1
    },
    skip: !selectedPerson
  });

  const { data, loading } = useOutletContext<JobsOutletContext>();

  const owner = data?.jobPost?.interactions?.find((interaction) => interaction.id === interactionId)?.owner;

  useEffect(() => {
    if (owner?.email) {
      setSelectedPerson(owner.email);
    }
  }, [owner?.email]);

  if (!sectionId || !jobPostId || !interactionId) {
    return (
      <div data-testid="error">
        <ErrorBoundary variant="inline" title="Invalid Page Access" message="Missing required parameters" />
      </div>
    );
  }

  if (loading && !data?.jobPost) {
    return <InteractionsPageSkeleton variant="detail" />;
  }

  if (!data?.jobPost) return null;

  return (
    <FocusedPageLayout backLink={backLink} onBackClick={onBackClick} actions={actions}>
      <InteractionDetails
        pageContext="Jobs"
        jobPostId={jobPostId}
        interactionId={interactionId}
        sectionId={sectionId}
        pgaId={pgaIdData?.people?.nodes[0]?.id}
        setActions={setActions}
        jobPost={data?.jobPost}
      />
    </FocusedPageLayout>
  );
}
